import React, { useEffect, useId, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { VERWERFEN } from '../../administration/components';
import { antwortVon, BESCHREIBUNG_MAX, hatFehler, meldeErgebnis, pruefeMeldung, zaehler, type FeldFehler, type MeldeErgebnis } from './anmeldeProblemLogik';
import { idListe } from './chatLogik';
import { meldeAnmeldeproblem } from './hilfeApi';
import { ANMELDEPROBLEM_TEXTE } from './hilfeTexte';

// „Problem bei der Anmeldung?“ auf der Anmeldeseite (Profil-Nachbau F-21, F21-5; PF-332): ein Formular ohne Konto im Rahmen der
// Anmeldeseite (LoginShell, deren Klassen). Der Server antwortet immer gleich, ob es zur Adresse ein Konto gibt oder nicht; erst
// der Link aus der Mail bestätigt die Anfrage (AnmeldeProblemBestaetigt.tsx). Escape und „Zurück zur Anmeldung“ fragen mit
// ungesicherten Eingaben „Eingaben verwerfen?“, ohne Eingaben geht es gleich zurück.
const T = ANMELDEPROBLEM_TEXTE;

export default function AnmeldeProblem({ email: vorbelegt, zurueck }: { email: string; zurueck: () => void }) {
  const [email, setEmail] = useState(vorbelegt);
  const [beschreibung, setBeschreibung] = useState('');
  const [felder, setFelder] = useState<FeldFehler>({});
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);
  const [erfolg, setErfolg] = useState(false);
  const [frage, setFrage] = useState(false);
  // Je Öffnen der Ansicht eine neue Kennung; ein Wiederholungsversuch schickt dieselbe, der Server legt dann nichts doppelt an.
  const [anfrageId] = useState(() => crypto.randomUUID());
  const emailId = useId(), beschreibungId = useId(), zaehlerId = useId(), hinweisId = useId();
  const ungesichert = !erfolg && (beschreibung.trim() !== '' || email.trim() !== vorbelegt.trim());
  const stand = useRef({ ungesichert, zurueck });
  stand.current = { ungesichert, zurueck };
  const verlassen = () => { if (stand.current.ungesichert) setFrage(true); else stand.current.zurueck(); };

  useEffect(() => {
    const taste = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;
      event.preventDefault();
      if (stand.current.ungesichert) setFrage(true); else stand.current.zurueck();
    };
    document.addEventListener('keydown', taste);
    return () => document.removeEventListener('keydown', taste);
  }, []);

  const anwenden = (ergebnis: MeldeErgebnis) => {
    if (ergebnis.art === 'erfolg') { setErfolg(true); setFrage(false); }
    else if (ergebnis.art === 'felder') setFelder(ergebnis.felder);
    else setFehler(ergebnis.satz);
  };
  const senden = async (event: React.FormEvent) => {
    event.preventDefault();
    if (sendet) return;
    const pruefung = pruefeMeldung(email, beschreibung);
    setFelder(pruefung);
    setFehler(null);
    if (hatFehler(pruefung)) return;
    setSendet(true);
    try {
      anwenden(meldeErgebnis(await meldeAnmeldeproblem({ email: email.trim(), beschreibung: beschreibung.trim(), anfrageId }), null));
    } catch (error) {
      const { status, daten } = antwortVon(error);
      anwenden(meldeErgebnis(status, daten));
    } finally {
      setSendet(false);
    }
  };

  const zaehlerText = zaehler(beschreibung.length);
  return <div className="login-form-wrap">
    <h1 id="auth-title">{T.titel}</h1>
    <p className="login-intro">{T.einleitung}</p>
    {erfolg
      ? <div className="login-notice success" role="status"><strong>{T.erfolgTitel}</strong><span>{T.erfolgText}</span></div>
      : <form className="login-form" noValidate onSubmit={event => void senden(event)} aria-busy={sendet}>
        <div className="login-field">
          <label htmlFor={emailId}>{T.email}</label>
          <input id={emailId} name="email" type="email" inputMode="email" value={email} autoComplete="email" autoCapitalize="none" spellCheck={false}
            aria-required="true" aria-invalid={felder.email ? true : undefined} aria-describedby={felder.email ? `${emailId}-fehler` : undefined}
            onChange={event => { setEmail(event.target.value); setFelder(vorher => ({ ...vorher, email: undefined })); }} />
          {felder.email && <p id={`${emailId}-fehler`} className="login-input-hint login-feld-fehler" role="alert">{felder.email}</p>}
        </div>
        <div className="login-field">
          <label htmlFor={beschreibungId}>{T.beschreibung}</label>
          <textarea id={beschreibungId} name="beschreibung" value={beschreibung} rows={5} maxLength={BESCHREIBUNG_MAX} aria-required="true"
            aria-invalid={felder.beschreibung ? true : undefined}
            aria-describedby={idListe(!!felder.beschreibung && `${beschreibungId}-fehler`, !!zaehlerText && zaehlerId, hinweisId)}
            onChange={event => { setBeschreibung(event.target.value); setFelder(vorher => ({ ...vorher, beschreibung: undefined })); }} />
          {zaehlerText && <p id={zaehlerId} className="login-input-hint">{zaehlerText}</p>}
          <p id={hinweisId} className="login-input-hint">{T.hinweis}</p>
          {felder.beschreibung && <p id={`${beschreibungId}-fehler`} className="login-input-hint login-feld-fehler" role="alert">{felder.beschreibung}</p>}
        </div>
        {fehler && <div className="login-notice error" role="alert"><span>{fehler}</span></div>}
        <button type="submit" disabled={sendet} className="login-submit">
          <span>{sendet ? T.sendet : T.senden}</span>{sendet ? <span className="login-state-indicator" aria-hidden="true" /> : <ArrowRight aria-hidden="true" />}
        </button>
      </form>}
    {frage && <div className="login-notice" role="alert">
      <strong>{VERWERFEN.frage}</strong>
      <span className="login-verwerfen">
        <button type="button" className="login-recovery" onClick={() => setFrage(false)}>{VERWERFEN.weiter}</button>
        <button type="button" className="login-recovery" onClick={zurueck}>{VERWERFEN.verwerfen}</button>
      </span>
    </div>}
    <p className="login-access"><button type="button" className="login-recovery" onClick={verlassen}>{T.zurueck}</button></p>
  </div>;
}
